import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import CategoryCard from '@/components/feature/CategoryCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import HomeFAQ from './components/HomeFAQ';
import { categories, getFeaturedArticles, getTrendingArticles } from '@/mocks/data';

export default function Home() {
  const { t } = useTranslation();
  const featuredArticles = getFeaturedArticles();
  const trendingArticles = getTrendingArticles();

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '反差设计研究所',
    url: 'https://boldniche.github.io',
    description: '专注探索反差美学在视觉设计、摄影、文案与品牌营销中的无限可能',
  };

  const webSiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '反差设计研究所',
    url: 'https://boldniche.github.io',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://boldniche.github.io/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '反差——视觉与情感的极致碰撞',
    description: '深度解析反差的本质、应用场景与创作技巧，帮助创作者掌握反差带来的强烈视觉冲击与情绪共鸣',
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webSiteSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <header className="relative w-full h-[500px] md:h-[620px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Dramatic%20artistic%20contrast%20composition%20bold%20warm%20golden%20light%20piercing%20through%20deep%20dark%20shadows%20abstract%20geometric%20forms%20minimalist%20elegant%20editorial%20design%20aesthetic%20soft%20gradient%20from%20warm%20amber%20to%20charcoal%20black%20cinematic%20atmosphere&width=1800&height=1000&seq=hero-main&orientation=landscape"
            alt="反差设计研究所"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/55"></div>
          <div className="relative z-10 text-center px-4 md:px-6 w-full max-w-3xl mx-auto">
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
              反差
              <span className="block text-2xl md:text-3xl lg:text-4xl font-normal mt-3 text-white/85">
                视觉与情感的极致碰撞
              </span>
            </h1>
            <p className="text-white/70 text-sm md:text-base mt-6 max-w-xl mx-auto leading-relaxed">
              「反差」是设计、摄影、文案、人设塑造中最强大的表达武器。深度解析反差的本质、应用场景与创作技巧，帮助创作者、品牌方、内容运营者掌握反差带来的强烈视觉冲击与情绪共鸣。
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
              <Link
                to="/category/contrast-visual-design"
                className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-primary-500 text-white font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer no-underline"
              >
                开始探索
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-white/15 backdrop-blur-sm text-white font-medium text-sm whitespace-nowrap hover:bg-white/25 transition-colors cursor-pointer no-underline"
              >
                了解我们
              </Link>
            </div>
          </div>
        </header>

        {/* Categories Section */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                探索反差的世界
              </h2>
              <p className="text-foreground-500 mt-3 text-sm md:text-base">
                六大维度，全方位拆解反差美学的底层逻辑与实战应用
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {categories.map((cat) => (
                <CategoryCard key={cat.slug} category={cat} />
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  {t('featuredArticles')}
                </h2>
                <p className="text-foreground-500 mt-2 text-sm">编辑精心挑选的深度好文</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {featuredArticles.slice(0, 3).map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </div>
        </section>

        {/* Trending Section */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  {t('trendingArticles')}
                </h2>
                <p className="text-foreground-500 mt-2 text-sm">读者们正在热议的话题</p>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {trendingArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} variant="horizontal" />
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-20 md:py-24 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20abstract%20gradient%20background%20with%20soft%20overlapping%20organic%20shapes%20amber%20and%20cream%20tones%20minimalist%20elegant%20texture%20subtle%20contrast%20editorial%20design%20aesthetic&width=1800&height=800&seq=cta-bg&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-background-900/70"></div>
          <div className="relative z-10 text-center px-4 md:px-6 max-w-2xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white">
              准备好让你的作品拥有"反差力"了吗？
            </h2>
            <p className="text-white/70 mt-4 text-sm md:text-base">
              每周更新深度原创内容，帮你从理论到实战，全面掌握反差美学的核心密码。加入我们，让你的创作从此告别平庸。
            </p>
            <Link
              to="/category/contrast-visual-design"
              className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-primary-500 text-white font-medium text-sm mt-8 whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer no-underline"
            >
              立即浏览全部文章
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </section>

        {/* FAQ Section */}
        <HomeFAQ />
      </main>

      <Footer />
    </>
  );
}